import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Loading from "../Loading/Loading";
import myAxios from "../AxiosUrl";
import styles from "./ModerationReports.module.css";

const MAX_RESOLUTION_LENGTH = 2000;
const REPORT_LIMIT = 100;

const statusOptions = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "reviewing", label: "Reviewing" },
  { value: "actioned", label: "Actioned" },
  { value: "dismissed", label: "Dismissed" },
];

const targetTypeOptions = [
  { value: "", label: "All targets" },
  { value: "message", label: "Messages" },
  { value: "user", label: "Users" },
];

const actionOptions = [
  {
    status: "reviewing",
    label: "Mark reviewing",
    icon: "visibility",
    className: styles.reviewingAction,
  },
  {
    status: "actioned",
    label: "Remove content & eject user",
    icon: "gavel",
    className: styles.actionedAction,
  },
  {
    status: "dismissed",
    label: "Dismiss report",
    icon: "cancel",
    className: styles.dismissedAction,
  },
];

const getReportId = (report) => report?._id || report?.id || "";

const formatLabel = (value) => {
  if (!value) return "Unknown";
  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getReviewDeadline = (createdAt) => {
  const timestamp = new Date(createdAt).getTime();
  return Number.isNaN(timestamp)
    ? null
    : new Date(timestamp + 24 * 60 * 60 * 1000);
};

const formatUser = (user) => {
  if (!user) return "Unknown user";
  if (typeof user === "string") return user;

  const displayName =
    user.name || user.username || user.email || user.displayName;
  const userId = user._id || user.id;

  if (displayName && userId) return `${displayName} (${userId})`;
  return displayName || userId || "Unknown user";
};

const toSafeText = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string") return value;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch (error) {
      return String(value);
    }
  }
  return String(value);
};

const normalizeError = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

const statusClassName = (status) => {
  if (status === "reviewing") return styles.statusReviewing;
  if (status === "actioned") return styles.statusActioned;
  if (status === "dismissed") return styles.statusDismissed;
  return styles.statusPending;
};

const ModerationReports = () => {
  const [reports, setReports] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [targetTypeFilter, setTargetTypeFilter] = useState("");
  const [selectedReportId, setSelectedReportId] = useState("");
  const [resolution, setResolution] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const latestRequestRef = useRef(0);

  const selectedReport = useMemo(
    () =>
      reports.find((report) => getReportId(report) === selectedReportId) ||
      null,
    [reports, selectedReportId]
  );

  const fetchReports = useCallback(
    async ({ silent = false } = {}) => {
      const requestId = latestRequestRef.current + 1;
      latestRequestRef.current = requestId;

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError("");

      try {
        const params = { limit: REPORT_LIMIT };
        if (statusFilter) params.status = statusFilter;
        if (targetTypeFilter) params.targetType = targetTypeFilter;

        const response = await myAxios.get("/moderation/reports", { params });
        if (latestRequestRef.current !== requestId) return;

        const nextReports = Array.isArray(response.data?.reports)
          ? response.data.reports
          : [];
        setReports(nextReports);
        setSelectedReportId((currentId) => {
          const currentStillExists = nextReports.some(
            (report) => getReportId(report) === currentId
          );
          return currentStillExists ? currentId : getReportId(nextReports[0]);
        });
      } catch (fetchError) {
        if (latestRequestRef.current !== requestId) return;
        setError(
          normalizeError(fetchError, "Failed to load moderation reports")
        );
      } finally {
        if (latestRequestRef.current === requestId) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [statusFilter, targetTypeFilter]
  );

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    setResolution(selectedReport?.resolution || "");
  }, [selectedReportId, selectedReport?.resolution]);

  const evidenceEntries = useMemo(() => {
    const evidence = selectedReport?.evidence;
    if (!evidence || typeof evidence !== "object") return [];
    return Object.entries(evidence).filter(
      ([, value]) => value !== null && value !== undefined && value !== ""
    );
  }, [selectedReport]);

  const handleStatusUpdate = async (nextStatus) => {
    if (!selectedReportId || updatingStatus) return;

    try {
      setUpdatingStatus(nextStatus);
      setError("");
      setSuccessMessage("");

      const trimmedResolution = resolution.trim();
      await myAxios.patch(`/moderation/reports/${selectedReportId}`, {
        status: nextStatus,
        resolution: trimmedResolution,
      });

      setReports((currentReports) =>
        currentReports.map((report) =>
          getReportId(report) === selectedReportId
            ? {
                ...report,
                status: nextStatus,
                resolution: trimmedResolution,
                reviewedAt: new Date().toISOString(),
              }
            : report
        )
      );
      setSuccessMessage(`Report marked as ${formatLabel(nextStatus)}.`);
      await fetchReports({ silent: true });
    } catch (updateError) {
      setError(
        normalizeError(updateError, "Failed to update moderation report")
      );
    } finally {
      setUpdatingStatus("");
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <p className={styles.kicker}>Trust &amp; safety</p>
          <h1>Moderation Reports</h1>
          <p>
            Review user and message reports, record a bounded internal
            resolution, and track the moderation outcome.
          </p>
        </div>
        <button
          type="button"
          className={styles.refreshButton}
          onClick={() => fetchReports({ silent: true })}
          disabled={loading || refreshing}
        >
          <span
            className={`material-icons ${refreshing ? styles.spin : ""}`}
            aria-hidden="true"
          >
            refresh
          </span>
          Refresh
        </button>
      </header>

      <section className={styles.filters} aria-label="Report filters">
        <label>
          <span>Status</span>
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setSuccessMessage("");
            }}
            disabled={loading}
          >
            {statusOptions.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Target type</span>
          <select
            value={targetTypeFilter}
            onChange={(event) => {
              setTargetTypeFilter(event.target.value);
              setSuccessMessage("");
            }}
            disabled={loading}
          >
            {targetTypeOptions.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <div className={styles.resultCount} aria-live="polite">
          <strong>{reports.length}</strong>
          <span>{reports.length === 1 ? "report shown" : "reports shown"}</span>
        </div>
      </section>

      {error && (
        <div className={`${styles.alert} ${styles.errorAlert}`} role="alert">
          <span className="material-icons" aria-hidden="true">
            error_outline
          </span>
          <span>{error}</span>
          <button type="button" onClick={() => fetchReports()}>
            Try again
          </button>
        </div>
      )}

      {successMessage && (
        <div
          className={`${styles.alert} ${styles.successAlert}`}
          role="status"
        >
          <span className="material-icons" aria-hidden="true">
            check_circle
          </span>
          <span>{successMessage}</span>
        </div>
      )}

      {loading ? (
        <Loading message="Loading moderation reports..." background="card" />
      ) : reports.length === 0 ? (
        <section className={styles.emptyState}>
          <span className="material-icons" aria-hidden="true">
            fact_check
          </span>
          <h2>No reports found</h2>
          <p>There are no moderation reports matching the selected filters.</p>
        </section>
      ) : (
        <div className={styles.workspace}>
          <section className={styles.listPanel} aria-label="Moderation reports">
            <div className={styles.panelHeader}>
              <div>
                <h2>Report queue</h2>
                <span>Newest first</span>
              </div>
              {refreshing && (
                <span className={styles.refreshingLabel}>Refreshing…</span>
              )}
            </div>

            <div className={styles.reportList}>
              {reports.map((report) => {
                const reportId = getReportId(report);
                const selected = reportId === selectedReportId;

                return (
                  <button
                    type="button"
                    key={reportId}
                    className={`${styles.reportCard} ${
                      selected ? styles.selectedCard : ""
                    }`}
                    onClick={() => {
                      setSelectedReportId(reportId);
                      setSuccessMessage("");
                    }}
                    aria-pressed={selected}
                  >
                    <div className={styles.cardTopRow}>
                      <span
                        className={`${styles.statusBadge} ${statusClassName(
                          report.status
                        )}`}
                      >
                        {formatLabel(report.status)}
                      </span>
                      <time dateTime={report.createdAt || ""}>
                        {formatDateTime(report.createdAt)}
                      </time>
                    </div>
                    <strong>
                      {formatLabel(report.targetType)} report:{" "}
                      {formatLabel(report.reason)}
                    </strong>
                    <span className={styles.cardUser}>
                      Target: {formatUser(report.targetUserId)}
                    </span>
                    <span className={styles.cardId}>ID: {reportId}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className={styles.detailPanel} aria-label="Report details">
            <div className={styles.detailHeader}>
              <div>
                <p className={styles.kicker}>Selected report</p>
                <h2>{formatLabel(selectedReport?.reason)}</h2>
              </div>
              <span
                className={`${styles.statusBadge} ${statusClassName(
                  selectedReport?.status
                )}`}
              >
                {formatLabel(selectedReport?.status)}
              </span>
            </div>

            <dl className={styles.metadataGrid}>
              <div>
                <dt>Target type</dt>
                <dd>{formatLabel(selectedReport?.targetType)}</dd>
              </div>
              <div>
                <dt>Reported at</dt>
                <dd>{formatDateTime(selectedReport?.createdAt)}</dd>
              </div>
              <div>
                <dt>24-hour deadline</dt>
                <dd>{formatDateTime(getReviewDeadline(selectedReport?.createdAt))}</dd>
              </div>
              <div>
                <dt>Reporter</dt>
                <dd>{formatUser(selectedReport?.reporterUserId)}</dd>
              </div>
              <div>
                <dt>Target user</dt>
                <dd>{formatUser(selectedReport?.targetUserId)}</dd>
              </div>
              <div>
                <dt>Message ID</dt>
                <dd className={styles.monospace}>
                  {selectedReport?.messageId || "-"}
                </dd>
              </div>
              <div>
                <dt>Room ID</dt>
                <dd className={styles.monospace}>
                  {selectedReport?.roomId || "-"}
                </dd>
              </div>
              <div>
                <dt>Content removed</dt>
                <dd>{formatDateTime(selectedReport?.contentRemovedAt)}</dd>
              </div>
              <div>
                <dt>User ejected</dt>
                <dd>{formatDateTime(selectedReport?.userEjectedAt)}</dd>
              </div>
            </dl>

            <div className={styles.section}>
              <h3>Reporter details</h3>
              <p className={styles.preservedText}>
                {selectedReport?.details || "No additional details supplied."}
              </p>
            </div>

            <div className={styles.section}>
              <h3>Captured evidence</h3>
              {evidenceEntries.length > 0 ? (
                <dl className={styles.evidenceList}>
                  {evidenceEntries.map(([key, value]) => (
                    <div key={key}>
                      <dt>{formatLabel(key)}</dt>
                      <dd>
                        <pre>{toSafeText(value)}</pre>
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className={styles.mutedText}>
                  No captured evidence is attached to this report.
                </p>
              )}
            </div>

            <div className={styles.section}>
              <div className={styles.sectionHeading}>
                <div>
                  <h3>Resolution</h3>
                  <p>Internal moderation notes sent with the selected outcome.</p>
                </div>
                <span>
                  {resolution.length}/{MAX_RESOLUTION_LENGTH}
                </span>
              </div>

              <label className={styles.resolutionField}>
                <span className={styles.visuallyHidden}>
                  Moderation resolution
                </span>
                <textarea
                  value={resolution}
                  onChange={(event) => setResolution(event.target.value)}
                  maxLength={MAX_RESOLUTION_LENGTH}
                  rows={6}
                  placeholder="Record what was reviewed and any action taken…"
                  disabled={Boolean(updatingStatus)}
                />
              </label>

              <div className={styles.actionButtons}>
                {actionOptions.map((action) => (
                  <button
                    type="button"
                    key={action.status}
                    className={action.className}
                    onClick={() => handleStatusUpdate(action.status)}
                    disabled={
                      Boolean(updatingStatus) ||
                      selectedReport?.status === action.status
                    }
                  >
                    <span className="material-icons" aria-hidden="true">
                      {updatingStatus === action.status
                        ? "sync"
                        : action.icon}
                    </span>
                    {updatingStatus === action.status
                      ? "Saving…"
                      : action.label}
                  </button>
                ))}
              </div>
            </div>

            {(selectedReport?.reviewedBy || selectedReport?.reviewedAt) && (
              <div className={styles.reviewAudit}>
                <span className="material-icons" aria-hidden="true">
                  verified_user
                </span>
                <div>
                  <strong>Last moderation review</strong>
                  <span>
                    {formatUser(selectedReport?.reviewedBy)} ·{" "}
                    {formatDateTime(selectedReport?.reviewedAt)}
                  </span>
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default ModerationReports;
